'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { NewsletterListItem, formatNewsletterDate, newsletterService } from '@/lib/newsletter/newsletterService';

export default function NewsletterArchive() {
  const [items, setItems] = useState<NewsletterListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    newsletterService
      .list()
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {
        if (!cancelled) setError('We could not load the newsletters just now. Please try again later.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <p role="alert" className="rounded-xl bg-white p-5 text-sm font-medium text-destructive">
        {error}
      </p>
    );
  }

  if (items === null) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading newsletters">
        {[0, 1, 2].map((i) => (
          <div key={`sk-${i}`} className="h-16 animate-pulse rounded-xl bg-white" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return <p className="rounded-xl bg-white p-5 text-sm text-[#2B0D18]/75">No editions have been published yet.</p>;
  }

  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.slug}>
          <Link
            href={`/newsletter/${encodeURIComponent(item.slug)}`}
            className="flex min-h-[64px] items-center justify-between gap-3 rounded-xl border border-[#C6A667]/40 bg-white px-4 py-3 shadow-sm transition-colors hover:border-[#C6A667] hover:bg-[#F8F3E9]/60"
          >
            <span className="min-w-0">
              <span className="block truncate text-base font-bold text-[#581C32]">{item.title}</span>
              {item.sentAt && (
                <span className="block text-sm text-[#2B0D18]/70">{formatNewsletterDate(item.sentAt)}</span>
              )}
            </span>
            <ChevronRight size={20} className="flex-shrink-0 text-[#C6A667]" aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ul>
  );
}
